<script setup lang="ts">
/**
 * 产品能力区
 * MV 停留由 App 扫描 data-mv-bid 统一管理；此处仅保留入场动效 + 视频懒加载。
 * 移动端/微信：串行加载 + 静音自动播放；微信拦截时挂起，Bridge/手势后自动续播。
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { prefersReducedMotion } from '../../utils/environment'
import { bindLazyVideo } from '../../utils/lazyVideo'
import { requestMutedAutoplay, playAllMutedSync, ensureMutedAutoplayUnlock } from '../../utils/mutedAutoplay'
import VideoPlayHint from './VideoPlayHint.vue'
import { LX_BIDS, type CapabilityItem } from './constants'

const props = defineProps<{
  capabilities: CapabilityItem[]
  isMobile: boolean
}>()

const rowRefs = ref<(HTMLElement | null)[]>([])
const videoRefs = ref<(HTMLVideoElement | null)[]>([])
/** 各视频是否在播（用于移动端播放三角显隐） */
const playingMap = ref<Record<number, boolean>>({})
let revealObserver: IntersectionObserver | undefined
const lazyCleanups: Array<() => void> = []
const autoplayCleanups: Array<() => void> = []

function setRowRef(element: unknown, index: number): void {
  if (element instanceof HTMLElement) rowRefs.value[index] = element
}

function setVideoRef(element: unknown, index: number): void {
  if (element instanceof HTMLVideoElement) videoRefs.value[index] = element
}

const onVideoPlay = (index: number) => {
  playingMap.value = { ...playingMap.value, [index]: true }
}

const onVideoPause = (index: number) => {
  playingMap.value = { ...playingMap.value, [index]: false }
}

/**
 * 移动端点击：在同一用户手势栈内同步拉起全部能力视频。
 * （微信要求 play() 必须同步调用，await 会丢掉手势授权）
 */
const onMediaTap = (index: number) => {
  if (!props.isMobile) return
  const video = videoRefs.value[index]
  if (!video) return

  // 已在播时再点：只暂停当前，不带动其它
  if (!video.paused && playingMap.value[index]) {
    video.pause()
    return
  }

  playAllMutedSync(videoRefs.value)
}

onMounted(() => {
  const rows = rowRefs.value.filter((el): el is HTMLElement => !!el)
  const skipMotion = props.isMobile || prefersReducedMotion()
  const mobile = props.isMobile

  if (mobile) ensureMutedAutoplayUnlock()

  props.capabilities.forEach((capability, index) => {
    lazyCleanups.push(
      bindLazyVideo(videoRefs.value[index], capability.video, {
        rootMargin: mobile ? '600px 0px' : '320px 0px',
        autoplay: true,
        sequential: mobile,
        onAutoplayBlocked: mobile
          ? (video) => {
              autoplayCleanups.push(requestMutedAutoplay(video))
            }
          : undefined
      })
    )
  })

  if (skipMotion) {
    rows.forEach((el) => el.classList.add('is-revealed'))
    return
  }

  if (!('IntersectionObserver' in window)) {
    rows.forEach((el) => el.classList.add('is-revealed'))
    return
  }

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-revealed', entry.intersectionRatio > 0)
      })
    },
    { threshold: [0, 0.01] }
  )
  rows.forEach((el) => revealObserver?.observe(el))
})

onBeforeUnmount(() => {
  revealObserver?.disconnect()
  lazyCleanups.splice(0).forEach((fn) => fn())
  autoplayCleanups.splice(0).forEach((fn) => fn())
})
</script>

<template>
  <section id="capSection" class="cap-section">
    <article
      v-for="(capability, index) in capabilities"
      :key="capability.title"
      :ref="(element) => setRowRef(element, index)"
      class="cap-row"
      :data-mv-bid="LX_BIDS.capabilityMv"
      :data-mv-key="`cap-${index + 1}`"
      :data-mv-module-identifier="`${index + 1}`"
    >
      <div class="cap-text">
        <div class="cap-title">
          <span :data-text="capability.title">{{ isMobile ? capability.title : '' }}</span>
        </div>
        <div class="cap-copy">
          <div class="cap-subtitle">{{ capability.subtitle }}</div>
          <div class="cap-desc" :data-text="isMobile ? undefined : capability.description">
            {{ isMobile ? capability.description : '' }}
          </div>
        </div>
      </div>
      <div
        class="capability-media"
        :class="{ 'is-playing': playingMap[index] }"
        :role="isMobile ? 'button' : undefined"
        :tabindex="isMobile ? 0 : -1"
        :aria-label="isMobile ? `播放${capability.title}演示视频` : undefined"
        @click="onMediaTap(index)"
      >
        <img
          v-if="isMobile"
          class="capability-poster"
          :class="{ 'is-hidden': playingMap[index] }"
          :src="capability.poster"
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
        >
        <video
          :ref="(element) => setVideoRef(element, index)"
          class="capability-video"
          :poster="isMobile ? capability.poster : undefined"
          muted
          :autoplay="isMobile"
          loop
          playsinline
          :webkit-playsinline="isMobile ? '' : undefined"
          :x5-playsinline="isMobile ? '' : undefined"
          :x5-video-player-type="isMobile ? 'h5' : undefined"
          :preload="isMobile ? 'metadata' : 'none'"
          disablepictureinpicture
          disableremoteplayback
          @playing="onVideoPlay(index)"
          @pause="onVideoPause(index)"
        />
        <VideoPlayHint v-if="isMobile && !playingMap[index]" />
      </div>
    </article>
  </section>
</template>

<style scoped lang="scss">
.cap-section {
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 0 auto;
  padding: 120px 0 0;
}

.cap-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 49px;
  min-height: 628px;
  margin: 0 0 120px;
  opacity: 0.08;
  transform: translateY(42px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.cap-row:nth-child(2) {
  gap: 100px;
}

.cap-row:nth-child(3) {
  gap: 130px;
}

.cap-row:nth-child(4) {
  gap: 132px;
  margin-bottom: 0;
}

.cap-row.is-revealed {
  opacity: 1;
  transform: none;
}

.cap-text {
  flex: 0 1 454px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.cap-title {
  margin: 0;
  color: #171717;
  font-size: 40px;
  font-weight: 650; /* DB1 */
  line-height: normal;
}

.cap-copy {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cap-subtitle {
  color: #171717;
  font-size: 24px;
  font-weight: 500;
  line-height: normal;
}

.cap-desc {
  max-width: 454px;
  margin: 0;
  color: #666;
  font-size: 16px;
  line-height: 1.6;
}

.capability-media {
  flex: none;
  width: 628px;
  height: 628px;
  overflow: hidden;
  border-radius: 69px;
  background: #f0f0f0;
}

.capability-poster,
.capability-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.capability-poster {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.capability-poster.is-hidden {
  opacity: 0;
}

@media (max-width: 960px) {
  .cap-section {
    width: auto;
    max-width: none;
    padding: 48px 20px 0;
  }

  .cap-row,
  .cap-row:nth-child(2),
  .cap-row:nth-child(3),
  .cap-row:nth-child(4) {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    min-height: auto;
    margin-bottom: 48px;
  }

  .cap-text {
    flex: none;
    width: 100%;
    gap: 16px;
  }

  .cap-title {
    font-size: 28px;
  }

  .cap-subtitle {
    font-size: 18px;
  }

  .capability-media {
    position: relative;
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    border-radius: 32px;
    cursor: pointer;
  }

  .capability-video {
    position: relative;
    z-index: 1;
    background: #e8e8e8;
  }
}
</style>
